import { GridCellParams, GridColDef, GridRenderCellParams, GridRowModel } from '@mui/x-data-grid-pro';
import { QuantityEditCell } from './PositiveIntegerEditCell';
import { renderCellWithDashDefault } from './CellRenderWithReset';
import AccountEditCell from './AccountEditCell';
import ProductEditCell from './ProductEditCell';
import SupplierEditCell from './SupplierEditCell';
import StatusEditCell from './StatusEditCell';
import { Account, Product, Supplier } from '../../../types';
import { getDate } from '../../../utils/helpers';

const smallColumnWidth = 80; // Minimum column width for smaller columns
const mediumColumnWidth = 120; // Minimum column width for medium columns
const largeColumnWidth = 200; // Minimum column width for larger columns

function getHighlightClass(params: GridCellParams) {
  const confidenceScore = params.row[`${params.colDef.field}_confidence`];
  if (confidenceScore < 0.4) {
    return 'low-confidence-cell';
  } else if (confidenceScore < 0.7) {
    return 'medium-confidence-cell';
  } else if (confidenceScore < 0.95) {
    return 'high-confidence-cell';
  }
  return '';
}

const PotentialInventoryColumnsDefs = (
  onReset: (row: GridRowModel) => void,
  accounts: Account[] = [],
  products: Product[] = [],
  suppliers: Supplier[] = [],
  showMRCWithoutCharges: boolean = false
): GridColDef[] => {
  return [
    {
      field: 'customerName',
      headerName: 'Customer *',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        // Custom reset handler for Customer Name that also resets customerId
        const handleCustomerReset = (row: GridRowModel) => {
          // Reset customerName and customerId to their original values
          params.api.updateRows([
            {
              id: params.id,
              customerName: row.customerName_original,
              customerId: row.customerId_original || ''
            }
          ]);
          // Call the original onReset callback with updated row
          if (onReset) {
            onReset({
              ...row,
              customerName: row.customerName_original,
              customerId: row.customerId_original || ''
            });
          }
        };
        return renderCellWithDashDefault(params, handleCustomerReset);
      },
      renderEditCell: (params) => (
        <AccountEditCell
          {...params}
          accounts={accounts}
        />
      ),
      cellClassName: getHighlightClass
    },
    {
      field: 'externalId',
      headerName: 'ID & Description *',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'providerAccountNumber',
      headerName: 'Provider Account Number',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'locationCode',
      headerName: 'Location Code',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'address',
      headerName: 'Address *',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'productName',
      headerName: 'Product *',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      valueFormatter: (value, row) =>
        row.product?.family ? `${row.product.family || ''} - ${row.productName}` : row.productName,
      renderCell: (params) => {
        // Custom reset handler for Product Name that also resets productId and product
        const handleProductReset = (row: GridRowModel) => {
          // Reset productName, productId, and product to their original values
          params.api.updateRows([
            {
              id: params.id,
              productName: row.productName_original,
              productId: row.productId_original || '',
              product: null // Clear the product object on reset
            }
          ]);
          // Call the original onReset callback with updated row
          if (onReset) {
            onReset({
              ...row,
              productName: row.productName_original,
              productId: row.productId_original || '',
              product: null
            });
          }
        };
        return renderCellWithDashDefault(params, handleProductReset);
      },
      renderEditCell: (params) => (
        <ProductEditCell
          {...params}
          products={products}
        />
      ),
      cellClassName: getHighlightClass
    },
    {
      field: 'supplierName',
      headerName: 'Supplier *',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        // Custom reset handler for Supplier Name that also resets supplierId and supplier
        const handleSupplierReset = (row: GridRowModel) => {
          // Reset supplierName, supplierId, and supplier to their original values
          params.api.updateRows([
            {
              id: params.id,
              supplierName: row.supplierName_original,
              supplierId: row.supplierId_original || '',
              supplier: null // Clear the supplier object on reset
            }
          ]);
          // Call the original onReset callback with updated row
          if (onReset) {
            onReset({
              ...row,
              supplierName: row.supplierName_original,
              supplierId: row.supplierId_original || '',
              supplier: null
            });
          }
        };
        return renderCellWithDashDefault(params, handleSupplierReset);
      },
      renderEditCell: (params) => (
        <SupplierEditCell
          {...params}
          suppliers={suppliers}
        />
      ),
      cellClassName: getHighlightClass
    },
    {
      field: 'accessProvider',
      headerName: 'Access Provider',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['Active', 'Installed', 'Pending Install', 'Disconnected', 'Cancelled', 'In Progress'],
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      renderEditCell: (params) => <StatusEditCell {...params} />,
      cellClassName: getHighlightClass
    },
    ...(showMRCWithoutCharges
      ? [
          {
            field: 'mRCWithoutCharges',
            headerName: 'MRC Without Charges*',
            flex: 1,
            type: 'number' as const,
            align: 'left' as const,
            headerAlign: 'left' as const,
            editable: true,
            minWidth: smallColumnWidth,
            renderCell: (params: GridRenderCellParams) => {
              return renderCellWithDashDefault(params, onReset);
            },
            valueFormatter: (value: number) => {
              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2
              }).format(value);
            },
            cellClassName: getHighlightClass
          }
        ]
      : [
          {
            field: 'mRC',
            headerName: 'MRC *',
            flex: 1,
            type: 'number' as const,
            align: 'left' as const,
            headerAlign: 'left' as const,
            editable: true,
            minWidth: smallColumnWidth,
            renderCell: (params: GridRenderCellParams) => {
              return renderCellWithDashDefault(params, onReset);
            },
            valueFormatter: (value: number) => {
              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2
              }).format(value);
            },
            cellClassName: getHighlightClass
          }
        ]),
    {
      field: 'nRC',
      headerName: 'NRC',
      flex: 1,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      editable: true,
      minWidth: smallColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      valueFormatter: (params) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2
        }).format(params);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'term',
      headerName: 'Term *',
      flex: 1,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['Month to Month', '12', '24', '36'],
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'completionDate',
      headerName: 'Completion Date',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      valueFormatter: (value) => {
        return value ? getDate(value).toLocaleDateString() : '';
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'serviceActivationDate',
      headerName: 'Service Activation Date',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      valueFormatter: (value) => {
        return value ? getDate(value).toLocaleDateString() : '';
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'contractEndDate',
      headerName: 'Contract End Date',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      valueFormatter: (value) => {
        return value ? getDate(value).toLocaleDateString() : '';
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'supplierOrderNumber',
      headerName: 'Supplier Order Number',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'disconnectedReason',
      headerName: 'Disconnected Reason',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'speedValue',
      headerName: 'Speed Value',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'speedUnit',
      headerName: 'Speed Unit',
      flex: 1,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['kbps', 'Mbps', 'Gbps'],
      minWidth: smallColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'circuitIDs',
      headerName: 'Circuit IDs',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'lecCircuitID',
      headerName: 'LEC Circuit ID',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'iPDetails',
      headerName: 'IP Details',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'rackType',
      headerName: 'Rack Type',
      flex: 1,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['Full', 'Partial'],
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'kwRack',
      headerName: 'kW Rack',
      flex: 1,
      editable: true,
      minWidth: smallColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'locationALecCircuitId',
      headerName: 'Location A LEC Circuit ID',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'locationALecDemarc',
      headerName: 'Location A LEC Demarc',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'locationALocalLec',
      headerName: 'Location A Local LEC',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'locationAText',
      headerName: 'Location A Text',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'locationZLecCircuitId',
      headerName: 'Location Z LEC Circuit ID',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'locationZLecDemarc',
      headerName: 'Location Z LEC Demarc',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'locationZLocalLec',
      headerName: 'Location Z Local LEC',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'locationZText',
      headerName: 'Location Z Text',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'equipmentManufacturer',
      headerName: 'Equipment Manufacturer',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'disconnectedDate',
      headerName: 'Disconnected Date',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      valueFormatter: (value) => {
        return value ? getDate(value).toLocaleDateString() : '';
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'lecDemarc',
      headerName: 'LEC Demarc',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'enclosure',
      headerName: 'Enclosure',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'floor',
      headerName: 'Floor',
      flex: 1,
      editable: true,
      minWidth: smallColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'focDate',
      headerName: 'FOC Date',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      valueFormatter: (value) => {
        return value ? getDate(value).toLocaleDateString() : '';
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'licenseQuantity',
      headerName: 'License Quantity',
      flex: 1,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(
          params,
          onReset,
          params.row.licenseQuantity >= 0,
          'License quantity must be a non-negative integer'
        );
      },
      renderEditCell: (params) => <QuantityEditCell {...params} />,
      cellClassName: getHighlightClass
    },
    {
      field: 'licenseExpirationDate',
      headerName: 'License Expiration Date',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      valueFormatter: (value) => {
        return value ? getDate(value).toLocaleDateString() : '';
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'metered',
      headerName: 'Metered',
      flex: 1,
      editable: true,
      minWidth: smallColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'model',
      headerName: 'Model',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'mount',
      headerName: 'Mount',
      flex: 1,
      editable: true,
      minWidth: smallColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'networkType',
      headerName: 'Network Type',
      flex: 1,
      editable: true,
      type: 'singleSelect',
      valueOptions: [
        'Ethernet',
        'ATM',
        'MPLS',
        'SD-WAN',
        'Frame Relay',
        'Leased Line',
        'Fiber Optic',
        'DSL',
        'ADSL',
        'ISDN'
      ],
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'pduPhase',
      headerName: 'PDU Phase',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'powerType',
      headerName: 'Power Type',
      flex: 1,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['Basic', 'Metered', 'Monitored', 'Switched'],
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'downloadSpeedMbps',
      headerName: 'Download Speed (Mbps)',
      flex: 1,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'uploadSpeedMbps',
      headerName: 'Upload Speed (Mbps)',
      flex: 1,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'circuitRole',
      headerName: 'Circuit Role',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'renewalLanguage',
      headerName: 'Renewal Language',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'voiceType',
      headerName: 'Voice Type',
      flex: 1,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['SIP', 'PRI', 'T1', 'E1', 'POTS', 'Mobile', 'PIAB'],
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'supplierSupportURL',
      headerName: 'Supplier Support URL',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        const url = params.value as string;
        if (url) {
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer">
              {url}
            </a>
          );
        }
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'networkDetails',
      headerName: 'Network Details',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'deviceDetails',
      headerName: 'Device Details',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'productSupportNumber',
      headerName: 'Product Support Number',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'supplierSupportNumber',
      headerName: 'Supplier Support Number',
      flex: 1,
      editable: true,
      minWidth: largeColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'customerLocationName',
      headerName: 'Location Name',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    },
    {
      field: 'customerLabelOther',
      headerName: 'Location Label',
      flex: 1,
      editable: true,
      minWidth: mediumColumnWidth,
      renderCell: (params) => {
        return renderCellWithDashDefault(params, onReset);
      },
      cellClassName: getHighlightClass
    }
  ];
};

export default PotentialInventoryColumnsDefs;
